import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { CalendarRange } from "lucide-react";
import { fmtPct } from "@/lib/verizonSalesApi";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const label = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;

const SERIES = [
  { key: "acts", label: "Activations", color: "#0d9488", bar: true },
  { key: "upgrades", label: "Upgrades", color: "#6366f1" },
  { key: "edge", label: "Edge", color: "#0ea5e9" },
  { key: "tablets", label: "Tablets", color: "#a855f7" },
  { key: "fwa", label: "FWA", color: "#ef4444" },
  { key: "twp_plus", label: "TWP+", color: "#16a34a", pct: true },
  { key: "topups", label: "Top-Ups", color: "#f97316" },
  { key: "autopay", label: "AutoPay", color: "#64748b", pct: true },
];

const Tip = ({ active, payload, on }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2 text-xs shadow">
      <div className="font-semibold text-navy mb-1">{label(d.month)}</div>
      {SERIES.filter((x) => on[x.key]).map((x) => (
        <div key={x.key}>{x.label}: <b>{d[x.key] == null ? "—" : x.pct ? fmtPct(d[x.key]) : d[x.key]}</b></div>
      ))}
      {on.acts && <div>Quota: <b>{d.quota ?? "n/a"}</b> · Attainment: <b>{fmtPct(d.attainment)}</b></div>}
      {d.goal != null && <div>Your goal: <b>{d.goal}</b></div>}
    </div>
  );
};

export const MonthlyHistoryChart = ({ monthly, title = "Monthly History", compact = false, testId = "vz-monthly-card" }) => {
  const [on, setOn] = useState({ acts: true });
  const data = (monthly || []).map((m) => ({ ...m, label: label(m.month) }));
  const anyPct = SERIES.some((x) => x.pct && on[x.key]);
  const anyCount = SERIES.some((x) => !x.pct && on[x.key]);
  const chart = (
    <div style={{ width: "100%", height: compact ? 180 : 280 }}>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 5, right: anyPct ? 0 : 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="label" fontSize={11} tick={{ fill: "#475569" }} />
          <YAxis yAxisId="n" hide={!anyCount && anyPct} allowDecimals={false} fontSize={11} tick={{ fill: "#475569" }} />
          {anyPct && <YAxis yAxisId="p" orientation="right" fontSize={11} tick={{ fill: "#475569" }} tickFormatter={(v) => `${Math.round(v * 100)}%`} />}
          <Tooltip content={<Tip on={on} />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {SERIES.filter((x) => on[x.key]).map((x) => (x.bar
            ? <Bar key={x.key} yAxisId="n" dataKey={x.key} name={x.label} fill={x.color} radius={[4, 4, 0, 0]} />
            : <Line key={x.key} yAxisId={x.pct ? "p" : "n"} dataKey={x.key} name={x.label} stroke={x.color} strokeWidth={2} dot={{ r: 3 }} connectNulls />))}
          {on.acts && <Line yAxisId="n" dataKey="quota" name="Quota" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />}
          {on.acts && data.some((m) => m.goal != null) && <Line yAxisId="n" dataKey="goal" name="Your Goal" stroke="#6366f1" strokeDasharray="5 4" strokeWidth={2} dot={{ r: 3 }} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
  const toggles = (
    <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`${testId}-toggles`}>
      {SERIES.map((x) => (
        <button key={x.key} type="button" onClick={() => setOn({ ...on, [x.key]: !on[x.key] })} data-testid={`${testId}-toggle-${x.key}`}
          aria-pressed={!!on[x.key]}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${on[x.key] ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
          <span className="w-2 h-2 rounded-full" style={{ background: x.color }} /> {x.label}
        </button>
      ))}
    </div>
  );
  if (compact) return <div data-testid={testId}>{data.length ? chart : <p className="text-xs text-slate-500">No monthly history yet.</p>}</div>;
  return (
    <Card className="border-slate-200" data-testid={testId}>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarRange className="w-4 h-4 text-teal" /> {title}</CardTitle></CardHeader>
      <CardContent>
        {data.length === 0 ? <p className="text-sm text-slate-500 py-10 text-center">No monthly history yet. Upload end-of-month DLAR files to backfill.</p> : <>{toggles}{chart}</>}
        {data.length > 0 && <p className="text-[11px] text-slate-500 mt-2">Tap a KPI above to show or hide it. Percentages (TWP+, AutoPay) use the right-hand axis. Quota appears with Activations for months loaded from a DLAR file. The current month is month-to-date.</p>}
      </CardContent>
    </Card>
  );
};
